import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { T, always, and, assoc, compose, cond, defaultTo, dissoc, equals, filter, has, ifElse, is, isEmpty, isNil, lensPath, lensProp, map, over, path, prop, propEq, propOr, propSatisfies, reject, toPairs, when, assocPath, uniq, difference, props, flatten, isNotNil, isNotEmpty, uniqBy, pluck, complement, startsWith, pathOr, find, forEach, any, includes, dissocPath, nth, identical } from 'ramda';
import { isNilOrEmpty, isNotNilOrEmpty, mapIndexed, notEqual } from 'ramda-adjunct';
import PreviewDocumentHtmlMutation from '../_graphql/mutations/documents/PreviewDocumentHtmlMutation';
import CreateDocumentMutation from '../_graphql/mutations/documents/CreateDocumentMutation';
import UpdateDocumentMutation from '../_graphql/mutations/documents/UpdateDocumentMutation';
import DeleteDocumentMutation from '../_graphql/mutations/documents/DeleteDocumentMutation';
import { BASE_PADDING_DOCUMENT, CLASSNAME_DOCUMENT_PORTAIT, DEFAULT_HEIGHT_PAGE_LANDSCAPE, DEFAULT_HEIGHT_PAGE_PORTRAIT, PREDEFINED_VARIABLES_FOR_RULES, SCOPE_OFFER } from '../_CONST';
import { customSuccessMessage, errorMessage, successMessage } from '../utils/messageMutation';
import UpdateCacheMutation from '../_graphql/mutations/UpdateCacheMutation';
import { getVariablesOnWidgetHtml } from '../pages/helpers/getVariablesOnWidgetHtml';
import GenerateStructuredHtmlMutation from '../_graphql/mutations/documents/GenerateStructuredHtmlMutation';
import { downloadHtmlFile } from '../utils/downloadHtmlFile';
import { getClassNameOrientation } from '../utils/getClassNameOrientation';
import { getVariablesForRules } from '../utils/getVariablesForRules';
import { ACTIONS, TARGET_TYPES, buildEntry } from '../lib/sessionHistory';
import { ROLES } from '../lib/rights';
import { hasRole } from '../components/users/utils/roles';
import { uploadFiles } from '../lib/uploadFiles';
import { compressImage } from '../lib/compressImage';
import { useWidgets } from './useWidgets';
import { defaultPage, useSections } from './useSections';
import useRules from './useRules';
import useSessionHistory from './useSessionHistory';

export const computeDocumentNameHistoryEntry = (previousName, nextName) => {
  if (notEqual(previousName, nextName)) {
    return buildEntry({
      action: ACTIONS.NAME_UPDATE,
      targetType: TARGET_TYPES.DOCUMENT,
      targetName: nextName,
      previousName
    });
  }
  return null;
};

// A padding change on the document itself is historised as a document-level
export const buildDocumentCompositionEntry = () => buildEntry({
  action: ACTIONS.COMPOSITION_UPDATE,
  targetType: TARGET_TYPES.DOCUMENT
});

// A widget created directly inside a document produces a CREATION entry attached
export const computeWidgetCreationEntry = (widget, placement = {}) => {
  const widgetId = prop('id', widget);
  if (isNil(widget) || isNil(widgetId)) return null;
  return buildEntry({
    action: ACTIONS.CREATION,
    targetType: TARGET_TYPES.WIDGET,
    widgetId,
    targetName: propOr(null, 'name', widget),
    sectionIndex: prop('sectionIndex', placement),
    block: prop('type', placement)
  });
};

// Purges history entries that are no longer valid based on the current state of pages and rules
const purgeInvalidRuleHistoryEntries = (pages, historyEntries, getConditionsAreValid) => {
  const isValidRule = (rule) =>
    isNotNil(prop('action', rule)) &&
    isNotNilOrEmpty(prop('conditions', rule)) &&
    getConditionsAreValid(prop('conditions', rule));

  const getPageWidgets = (page) =>
    [...propOr([], 'header', page), ...propOr([], 'widgets', page), ...propOr([], 'footer', page)];

  const validWidgetIds = compose(
    uniq,
    flatten,
    map(page => compose(
      map(prop('widgetId')),
      filter(widget => any(isValidRule, propOr([], 'rules', widget)))
    )(getPageWidgets(page)))
  )(pages);

  const validSectionIndexes = compose(
    reject(isNil),
    mapIndexed((page, index) =>
      isNotNilOrEmpty(prop('rules', page)) && any(isValidRule, propOr([], 'rules', page)) ? index : null)
  )(pages);

  return filter((entry) => {
    if (notEqual(prop('action', entry), ACTIONS.CONDITIONS_UPDATE)) return true;
    if (propEq(TARGET_TYPES.SECTION, 'targetType', entry)) return includes(prop('sectionIndex', entry), validSectionIndexes);
    if (propEq(TARGET_TYPES.WIDGET, 'targetType', entry)) return includes(prop('widgetId', entry), validWidgetIds);
    return true;
  }, historyEntries);
};

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(new Error('Lecture fichier échouée'));
  reader.readAsDataURL(file);
});

// Builds GraphQL pages by removing pendingFile, uploading to /files on save, and using base64 only for preview.
const resolvePagesForMutation = async (pages, options = {}) => {
  const { persistUrl = false } = options;
  return Promise.all(pages.map(async (page) => {
    const pendingFile = path(['backgroundImage', 'pendingFile'], page);
    let resolvedPage = dissocPath(['backgroundImage', 'pendingFile'], page);
    if (isNotNil(pendingFile)) {
      try {
        if (persistUrl) {
          const { fileId } = await uploadFiles(pendingFile);
          const url = `/files/${fileId}`;
          resolvedPage = compose(
            assocPath(['backgroundImage', 'url'], url),
            dissocPath(['backgroundImage', 'pendingFile'])
          )(page);
        } else {
          const base64Url = await fileToBase64(await compressImage(pendingFile));
          resolvedPage = compose(
            assocPath(['backgroundImage', 'url'], base64Url),
            dissocPath(['backgroundImage', 'pendingFile'])
          )(page);
        }
      } catch (error) {
        // Do not silently drop the background image: surface the failure and
        // stop the save, rather than persisting a document amputated of its background.
        errorMessage('Échec de l\'envoi de l\'image de fond, veuillez réessayer.');
        throw error;
      }
    }
    return resolvedPage;
  }));
};

const useDocument = (
  document = {},
  options = {},
  comments,
  globalVariablesFetch = [],
  predefinedVariables,
  widgetsFetch,
  setRefetch,
  userTeam
) => {
  const [widgets, setWidgets] = useState(widgetsFetch);
  const [hasLandscapeWidget, setHasLandscapeWidget] = useState(false);
  const [widgetIdEditing, setWidgetIdEditing] = useState();
  const [highlightedWidgetId, setHighlightedWidgetId] = useState(null);
  const [widgetEditOrigin, setWidgetEditOrigin] = useState(null);
  const [widgetCreated, setWidgetCreated] = useState();
  const [isOpenCreateWidgetModal, setIsOpenCreateWidgetModal] = useState(false);
  const [duplicateWidgetPlacement, setDuplicateWidgetPlacement] = useState(false);
  const [globalVariableCreated, setGlobalVariableCreated] = useState();
  const [isOpenCreateGlobalVariableModal, setIsOpenCreateGlobalVariableModal] = useState(null);
  const [globalVariables, setGlobalVariables] = useState(globalVariablesFetch);
  const [variablesForRules, setVariablesForRules] = useState(getVariablesForRules([...globalVariablesFetch, ...PREDEFINED_VARIABLES_FOR_RULES]));
  const [selectedTags, setSelectedTags] = useState({
    risks: [],
    ranges: [],
    documentTypes: []
  });
  const [isOpenTagModal, setIsOpenTagModal] = useState(false);
  const quitRef = useRef(false);

  const offerId = useMemo(() => prop('offerId', options), [options]);
  const userIsOfferEditor = useMemo(() => hasRole(ROLES.offerEditor, propOr([], 'roles', userTeam), offerId), [offerId, userTeam]);

  useEffect(() => {
    if (isNotNil(globalVariableCreated)) {
      const { onUpdate, conditionIndex } = isOpenCreateGlobalVariableModal;
      onUpdate(assocPath(['conditions', conditionIndex, 'variable'], prop('name', globalVariableCreated)));
      setGlobalVariables([...globalVariables, globalVariableCreated]);
      setGlobalVariableCreated();
      setIsOpenCreateGlobalVariableModal(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalVariableCreated]);

  useEffect(() => {
    if (isNotNilOrEmpty(globalVariablesFetch)) {
      setGlobalVariables(globalVariablesFetch);
    }
  }, [globalVariablesFetch]);
   
  useEffect(() => setVariablesForRules(getVariablesForRules([...globalVariables, ...PREDEFINED_VARIABLES_FOR_RULES])), [globalVariables]);

  useEffect(() => {
    if (isNotNil(widgetCreated)) {
      const variables = getVariablesOnWidgetHtml(JSON.parse(propOr([], 'contents', widgetCreated)), globalVariables);
      setWidgets([{ ...widgetCreated, variables }, ...widgets]);
      UpdateCacheMutation({ date: new Date(), key: `widgets-${offerId}` }, () => {});
      const globalVariablesNames = map(prop('name'), globalVariables);
      // The freshly created/duplicated widget is not yet in the `widgets` list captured
      // by useWidgets this render, so pass its name explicitly to keep the ADDITION
      // entry's required targetName populated.
      const newWidgetName = propOr(null, 'name', widgetCreated);
      if (isOpenCreateWidgetModal) {
        const { rowIndex, index, sectionIndex, type } = isOpenCreateWidgetModal;
        pushEvent(computeWidgetCreationEntry(widgetCreated, { sectionIndex, type }));
        updateWidget(globalVariablesNames, sectionIndex, type)(rowIndex, index, prop('id', widgetCreated), newWidgetName);
        setWidgetCreated();
        setIsOpenCreateWidgetModal(false);
      } else {
        const { rowIndex, index, sectionIndex, type } = duplicateWidgetPlacement;
        updateWidget(globalVariablesNames, sectionIndex, type)(rowIndex, index, prop('id', widgetCreated), newWidgetName);
        setWidgetCreated();
        setDuplicateWidgetPlacement(false);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgetCreated]);

  const [rowIndexEditing, setRowIndexEditing] = useState([]);
  const isEdit = compose(
    isNotNil,
    prop('id')
  )(document);

  const history = useHistory();

  const { pushEvent, getStack, clear, setBaseline, markStructuralChange } = useSessionHistory();

  useEffect(() => () => clear(), [clear]);

  const getInfoPageContent = (p, type) => {
    return over(
      lensProp(type),
      compose(reject(propSatisfies(isEmpty, 'header')), defaultTo([]))
    )(p);
  };
  const [state, setState] = useState({
    values: {
      name: propOr('', 'name', document),
      pages: compose(
        map(
          compose(
            (p) => getInfoPageContent(p, 'header'),
            (p) => getInfoPageContent(p, 'widgets'),
            (p) => getInfoPageContent(p, 'footer'),
            (p) => assoc('breakPage', propOr(true, 'breakPage', p), p),
            (p) => assoc('padding', propOr(BASE_PADDING_DOCUMENT, 'padding', p), p),
            (p) => assoc('rules', propOr([], 'rules', p), p),
            (p) => assoc('backgroundImage', {
              url: '',
              size: 'contain',
              position: 'center center',
              pendingFile: null,
              ...propOr({}, 'backgroundImage', p)
            }, p)
          )
        ),
        defaultTo([defaultPage]),
        prop('pages')
      )(document),
      variables: compose(
        map((v) => ifElse(
          has('rows'),
          () => compose(
            dissoc('rows'),
            assoc('value', { rows: prop('rows', v) }),
            assoc('type', 'JSON')
          )(v),
          always(v)
        )(v)),
        JSON.parse,
        propOr('{}', 'variables')
      )(document),
      padding: propOr(BASE_PADDING_DOCUMENT, 'padding', document),
      orientation: propOr('portrait', 'orientation', document),
      renderingEngineVersion: propOr('v2', 'renderingEngineVersion', document),
      formatPdf: propOr('PDF', 'formatPdf', document),
      isVariablesHighlighted: propOr(false, 'isVariablesHighlighted', document),
      isHDS: propOr(false, 'isHDS', document)
    },
    loading: false,
    deleteLoading: false,
    previewLoading: false,
    previewHtmlLoading: false,
    previewHtml: undefined,
    documentPreviewUrl: undefined,
    downloadStructuredHtmlLoading: false
  });
  
  const savingRef = useRef(false);
  const stateRef = useRef(state);
  stateRef.current = state;

  // Snapshot the placements the persisted document starts with: a widget deletion
  // is only journaled when the widget was already there. Removing a widget added
  // during the same session is a net no-op and must leave no trace
  useEffect(() => {
    setBaseline(path(['values', 'pages'], state));
    // Mount-only snapshot: `state` must never re-trigger it, so the rule has
    // nothing useful to enforce here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    getRulesAreValid,
    allRulesAreValid,
    getConditionsAreValid
  } = useRules(state);
  
  const {
    widgetsAreDefined,
    addWidget,
    addRowWidget,
    removeWidget,
    updateWidget,
    moveRowWidget,
    updateBreakPage,
    addWidgetRule,
    removeWidgetRule,
    updateWidgetRule,
    updateManualBreakPage,
    updatePaddingRow,
    addManualBreakPageRule,
    removeManualBreakPageRule,
    updateManualBreakPageRule
  } = useWidgets(state, setState, getConditionsAreValid, pushEvent, widgets, markStructuralChange);

  const {
    editSections,
    updatePaddingSection,
    setPendingBackgroundImageSection,
    clearBackgroundImageSection,
    updateBackgroundSizeSection,
    updateBackgroundPositionSection,
    addRuleSection,
    updateRuleSection,
    removeRuleSection
  } = useSections(state, setState, pushEvent);

  const pages = path(['values', 'pages'], state);
  const variablesState = pathOr({}, ['values', 'variables'], state);
  const {
    variables,
    widgetsOnDocument
  } = useMemo(() => {
    const getWidgetById = widgetId => find(propEq(widgetId, 'id'), widgets || []);
    const widgetsOnDocument = compose(
      uniq,
      flatten,
      map(page => compose(
        map(({ widgetId }) => getWidgetById(widgetId)),
        reject(propSatisfies(isNil, 'widgetId')),
        uniqBy(prop('widgetId')),
        reject(isNil),
        compose(flatten, pluck('widgets')),
        flatten
      )([prop('header', page), prop('footer', page), prop('widgets', page)]))
    )(pages);

    const variablesWidgetsOnDocument = compose(
      uniq,
      reject(widget => isNil(widget) || propEq('rule', 'type', widget)),
      flatten,
      pluck('variables')
    )(widgetsOnDocument);

    const variables = compose(
      filter(propSatisfies(complement(startsWith('_summary-')), 'key')),
      filter(v => !map(prop('key'))(predefinedVariables).includes(prop('key', v)))
    )(variablesWidgetsOnDocument);

    return {
      variables,
      widgetsOnDocument
    };
  }, [pages, widgets, predefinedVariables]);

  useEffect(() => {
    compose(
      map(([sectionIndex, section]) => map((widgets) => compose(
        map(([rowIndex, row]) => {
          const paddingRow = prop('padding', row);
          when(
            isNil,
            () => setState(over(
              lensPath(['values', 'pages', Number(sectionIndex), 'widgets', Number(rowIndex)]),
              assoc('padding', BASE_PADDING_DOCUMENT)
            ))
          )(paddingRow);
        }),
        filter(([_, row]) => and(is(Object, row), prop('widgets', row))),
        toPairs
      )(widgets))(section)),
      toPairs
    )(pages);
  }, [pages]);

  useEffect(() => {
    forEach((variable) => {
      const key = prop('key', variable);
      const defaultValue = prop('default', variable);
      if (!has(key, variablesState) && isNotNil(defaultValue)) {
        setState(assocPath(['values', 'variables', key], {
          value: defaultValue,
          type: prop('type', variable)
        }));
      }
    }, variables);
  }, [variablesState, variables]);

  const [classNameOrientationDocument, setClassNameOrientationDocument] = useState(CLASSNAME_DOCUMENT_PORTAIT);
  const [pageHeightDocument, setPageHeightDocument] = useState(DEFAULT_HEIGHT_PAGE_PORTRAIT);

  const previewDocumentHtml = useCallback(async () => {
    setState(assoc('previewHtmlLoading', true));
    setState(assoc('previewHtml', undefined));
    setState(assoc('documentPreviewUrl', undefined));

    // Temporary processing of pendingFile for preview
    let resolvedPages;
    try {
      resolvedPages = await resolvePagesForMutation(path(['values', 'pages'], state));
    } catch (_) {
      setState(assoc('previewHtmlLoading', false));
      return;
    }

    PreviewDocumentHtmlMutation(
      {
        pages: resolvedPages,
        variables: path(['values', 'variables'], state),
        documentId: propOr('', 'id', document),
        padding: path(['values', 'padding'], state),
        orientation: path(['values', 'orientation'], state),
        renderingEngineVersion: path(['values', 'renderingEngineVersion'], state),
        isVariablesHighlighted: path(['values', 'isVariablesHighlighted'], state),
        isInteractive: true
      },
      (ok, error, html) => {
        setState(assoc('previewHtmlLoading', false));

        if (ok && !error) {
          setState(assoc('previewHtml', html));

          setClassNameOrientationDocument(getClassNameOrientation(path(['values', 'orientation'], state)));

          setPageHeightDocument(cond([
            [equals('portrait'), always(DEFAULT_HEIGHT_PAGE_PORTRAIT)],
            [equals('landscape'), always(DEFAULT_HEIGHT_PAGE_LANDSCAPE)],
            [T, always(DEFAULT_HEIGHT_PAGE_PORTRAIT)]
          ])(path(['values', 'orientation'], state)));
        } else {
          errorMessage();
        }
      }
    );
  }, [document, state]);

  const downloadStructuredHtml = async () => {
    setState(assoc('downloadStructuredHtmlLoading', true));

    let resolvedPages;
    try {
      resolvedPages = await resolvePagesForMutation(path(['values', 'pages'], state));
    } catch (_) {
      setState(assoc('downloadStructuredHtmlLoading', false));
      return;
    }

    GenerateStructuredHtmlMutation(
      {
        offerId,
        documentId: prop('id', document),
        pages: resolvedPages,
        variables: path(['values', 'variables'], state),
        padding: path(['values', 'padding'], state)
      },
      (ok, error, html, filename) => {
        setState(assoc('downloadStructuredHtmlLoading', false));

        if (ok && !error) {
          downloadHtmlFile(html, filename);
        } else {
          errorMessage();
        }
      }
    );
  };

  const updatePaddingDocument = (key, value) => {
    pushEvent(buildDocumentCompositionEntry());
    setState(over(
      lensPath(['values', 'padding']),
      assoc(key, value)
    ));
  };

  const updateIsVariablesHighlighted = (isVariablesHighlighted) => {
    setState(assocPath(['values', 'isVariablesHighlighted'], isVariablesHighlighted));
    previewDocumentHtml();
    customSuccessMessage(isVariablesHighlighted ? 'Les variables sont désormais surlignées' : 'Les variables ne sont plus surlignées');
  };

  const redirect = () => history.push(`/offre/${offerId}/edition`);

  const onSave = async (quit = false, ignoreTags = false) => {
    if (savingRef.current) return false;
    savingRef.current = true;
    setState(assoc('loading', true));

    const pagesSnapshot = path(['values', 'pages'], state);
    let resolvedBackgroundPages;
    try {
      resolvedBackgroundPages = await resolvePagesForMutation(pagesSnapshot, { persistUrl: true });
    } catch (_) {
      savingRef.current = false;
      setState(assoc('loading', false));
      return false;
    }
    const currentValues = path(['current', 'values'], stateRef);
    let pages = map((page) => {
      const currentFile = path(['backgroundImage', 'pendingFile'], page);
      const snapshotIndex = pagesSnapshot.findIndex(snapshotPage => {
        const match = isNotNil(currentFile) && identical(path(['backgroundImage', 'pendingFile'], snapshotPage), currentFile);
        return match;
      });
      if (snapshotIndex < 0) {
        if (isNotNil(currentFile)) return page;
        page = dissocPath(['backgroundImage', 'pendingFile'], page);
        return page;
      }
      return compose(
        dissocPath(['backgroundImage', 'pendingFile']),
        assocPath(['backgroundImage', 'url'], path(['backgroundImage', 'url'], nth(snapshotIndex, resolvedBackgroundPages)))
      )(page);
    }, propOr([], 'pages', currentValues));
    if (any(page => isNotNil(path(['backgroundImage', 'pendingFile'], page)), pages)) {
      savingRef.current = false;
      setState(assoc('loading', false));
      errorMessage('L\'image de fond a changé pendant l\'envoi, veuillez sauvegarder à nouveau.');
      return false;
    }

    const updateRules = over(
      lensProp('rules'),
      compose(
        reject(rule => isNil(rule.action) || isNilOrEmpty(rule.conditions) || !getConditionsAreValid(rule.conditions)),
        defaultTo([])
      )
    );

    const updateTypeContent = (type) => over(
      lensProp(type),
      map(
        over(
          lensProp('widgets'),
          map(updateRules)
        )
      )
    );
    pages = map(compose(
      updateTypeContent('header'),
      updateTypeContent('widgets'),
      updateTypeContent('footer'),
      updateRules
    ))(pages);
    
    const pagesWithRemovedEmptyRow = map((page) => {
      const updateTypeContent = (type) => over(
        lensProp(type),
        compose(
          filter((widget) => isNotEmpty(prop('widgets', widget))),
          map(
            over(
              lensProp('widgets'),
              reject((widget) => isNil(prop('widgetId', widget)))
            )
          )
        )
      );

      return compose(
        updateTypeContent('header'),
        updateTypeContent('widgets'),
        updateTypeContent('footer')
      )(page);
    })(pages);
    pages = pagesWithRemovedEmptyRow;
    setState(assocPath(['values', 'pages'], pages));

    const callback = (successMessage, isCreation) => (ok, error, documentId) => {
      savingRef.current = false;
      setState(assoc('loading', false));

      if (ok && !error) {
        customSuccessMessage(successMessage);

        if (propEq(SCOPE_OFFER, 'scope', options)) {
          const newName = propOr('', 'name', currentValues);
          const nameHasChanged = notEqual(prop('name', document), newName);
          if (nameHasChanged || !isEdit) {
            UpdateCacheMutation({ date: new Date(), key: `documents-${offerId}` }, () => {});
          }

          const getWidgetIds = (data) => {
            return data.reduce((acc, item) => {
              if (item.widgets) {
                acc = acc.concat(getWidgetIds(item.widgets));
              }
              if (item.footer) {
                acc = acc.concat(getWidgetIds(item.footer));
              }
              if (item.header) {
                acc = acc.concat(getWidgetIds(item.header));
              }
              if (item.widgetId) {
                acc.push(item.widgetId);
              }
              return acc;
            }, []);
          };

          const actualWidgetIds = uniq(getWidgetIds(pages));
          const oldWidgetIds = uniq(getWidgetIds(propOr([], 'pages', document)));
          if (isNotEmpty(difference(actualWidgetIds, oldWidgetIds))) {
            UpdateCacheMutation({ date: new Date(), key: `widgets-${offerId}` }, () => {});
          }
        }

        const quitValue = quit || quitRef.current;

        if (!quitValue && isCreation) {
          history.push(`/offre/${documentId}/editer`);
          return;
        }
        quitValue && redirect();
      } else {
        errorMessage();
      }
    };

    if (isEdit) {
      const nameEntry = computeDocumentNameHistoryEntry(prop('name', document), propOr('', 'name', currentValues));
      if (nameEntry) pushEvent(nameEntry);
      // Snapshot + clear the stack BEFORE the network call: pushEvent/clear are
      // synchronous on the same stackRef, so this closes two bugs at once —
      // a failed save no longer leaves nameEntry/other entries in the stack to be
      // duplicated on the next save, and a double-click during the in-flight
      // request can no longer send the same entries twice.
      const stack = getStack();
      const historyEntries = purgeInvalidRuleHistoryEntries(pages, stack, getConditionsAreValid);
      clear();
      // The saved pages become the new session baseline: widgets added before this
      // save are now persisted, so removing them later is a genuine deletion.
      setBaseline(pages);
      UpdateDocumentMutation({
        documentId: prop('id', document),
        name: propOr('', 'name', currentValues),
        pages,
        variables: JSON.stringify(propOr({}, 'variables', currentValues)),
        padding: prop('padding', currentValues),
        orientation: prop('orientation', currentValues),
        renderingEngineVersion: prop('renderingEngineVersion', currentValues),
        formatPdf: prop('formatPdf', currentValues),
        isVariablesHighlighted: prop('isVariablesHighlighted', currentValues),
        isHDS: prop('isHDS', currentValues),
        historyEntries
      }, (ok, error, documentId) => {
        callback('Le document a bien été mis à jour.')(ok, error, documentId);
      });
    } else {
      const tagIds = ignoreTags ? [] : compose(
        filter(isNotNilOrEmpty),
        flatten,
        props(['risks', 'ranges', 'documentTypes'])
      )(selectedTags);
      CreateDocumentMutation({
        name: propOr('', 'name', currentValues),
        pages,
        scope: prop('scope', options),
        offerId,
        variables: JSON.stringify(propOr({}, 'variables', currentValues)),
        padding: prop('padding', currentValues),
        orientation: prop('orientation', currentValues),
        renderingEngineVersion: prop('renderingEngineVersion', currentValues),
        formatPdf: prop('formatPdf', currentValues),
        isHDS: prop('isHDS', currentValues),
        tagIds
      }, callback('Le document a bien été créé.', true));
    }
  };

  const handleSaveBtn = (quit) => {
    if (isEdit) {
      onSave(quit, true);
    } else {
      quitRef.current = quit;
      setIsOpenTagModal(true);
    }
  };

  const saveDocumentAndTags = (ignore) => {
    if (!isEdit) {
      setIsOpenTagModal(false);
    }
    onSave(quitRef.current, ignore);
  };

  const onDelete = () => {
    if (isEdit) {
      setState(assoc('deleteLoading', true));

      DeleteDocumentMutation({ documentId: prop('id', document) }, (ok, error) => {
        setState(assoc('deleteLoading', false));

        if (ok && !error) {
          successMessage('document', 'supprimé');
          if (propEq(SCOPE_OFFER, 'scope', options)) {
            UpdateCacheMutation({ date: new Date(), key: `documents-${offerId}` }, () => {});
            if (isNotEmpty(path(['values', 'pages'], state))) {
              UpdateCacheMutation({ date: new Date(), key: `widgets-${offerId}` }, () => {});
            }
          }
          redirect();
        } else {
          errorMessage();
        }
      });
    }
  };

  const updateSelectedTags = (type, value) => setSelectedTags(assoc(type, value));

  const nameIsDefined = compose(isNotNilOrEmpty, path(['values', 'name']))(state);

  const title = `${isEdit ? 'Créer' : 'Éditer'} un document`;

  return {
    setState,
    addWidget,
    addRowWidget,
    removeWidget,
    updateWidget,
    addWidgetRule,
    moveRowWidget,
    updateBreakPage,
    removeWidgetRule,
    updateWidgetRule,
    previewDocumentHtml,
    onSave,
    pushEvent,
    handleSaveBtn,
    saveDocumentAndTags,
    onDelete,
    widgetsAreDefined,
    nameIsDefined,
    isEdit,
    getRulesAreValid,
    allRulesAreValid,
    getConditionsAreValid,
    title,
    updateManualBreakPage,
    updatePaddingRow,
    editSections,
    updatePaddingDocument,
    updatePaddingSection,
    setPendingBackgroundImageSection,
    clearBackgroundImageSection,
    updateBackgroundSizeSection,
    updateBackgroundPositionSection,
    addRuleSection,
    updateRuleSection,
    removeRuleSection,
    setRefetch,
    widgetIdEditing,
    setWidgetIdEditing,
    highlightedWidgetId,
    setHighlightedWidgetId,
    widgetEditOrigin,
    setWidgetEditOrigin,
    rowIndexEditing,
    setRowIndexEditing,
    hasLandscapeWidget,
    setHasLandscapeWidget,
    addManualBreakPageRule,
    removeManualBreakPageRule,
    updateManualBreakPageRule,
    classNameOrientationDocument,
    pageHeightDocument,
    scope: prop('scope', options),
    offerId,
    documentId: prop('id', document),
    document,
    comments: propOr([], 'comments', comments),
    widgets,
    setWidgets,
    globalVariables,
    setGlobalVariables,
    predefinedVariables,
    setWidgetCreated,
    isOpenCreateWidgetModal,
    setIsOpenCreateWidgetModal,
    isOpenCreateGlobalVariableModal,
    setIsOpenCreateGlobalVariableModal,
    setGlobalVariableCreated,
    updateIsVariablesHighlighted,
    setDuplicateWidgetPlacement,
    downloadStructuredHtml,
    variablesForRules,
    updateSelectedTags,
    isOpenTagModal,

    // State
    values: prop('values', state),
    loading: prop('loading', state),
    deleteLoading: prop('deleteLoading', state),
    previewLoading: prop('previewLoading', state),
    previewHtmlLoading: prop('previewHtmlLoading', state),
    downloadStructuredHtmlLoading: prop('downloadStructuredHtmlLoading', state),
    previewHtml: prop('previewHtml', state),
    documentPreviewUrl: prop('documentPreviewUrl', state),
    orientation: path(['values', 'orientation'], state),
    renderingEngineVersion: path(['values', 'renderingEngineVersion'], state),
    formatPdf: path(['values', 'formatPdf'], state),
    isHDS: path(['values', 'isHDS'], state),
    riskTags: prop('risks', selectedTags),
    rangeTags: prop('ranges', selectedTags),
    documentTypeTags: prop('documentTypes', selectedTags),
    tags: selectedTags,

    // variables
    widgetsOnDocument,
    variables,

    // rights
    userIsOfferEditor
  };
};

export default useDocument;
